import React, { useEffect, useRef, useState } from "react";
import { GetAppText } from "../../../../utils";
import { useSelector } from "react-redux";
import { NewsCardComponent } from "../../../components/NewsCardComponent";
import { Loader } from "../../../components/Loader";
import { useNavigate } from 'react-router-dom';
import "../../../../App.css"
import SvgComponent from "../../../components/SvgComponent";
import { getRequest } from "../../../../apis/methods";
import { apiRoutes } from "../../../../apis/routes";

// Cards visible at once: 3 on desktop, 2 on tablet, 1 on mobile.
const getPerView = () => {
  if (typeof window === "undefined") return 3;
  if (window.innerWidth >= 1024) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
};
const getGap = () => (typeof window !== "undefined" && window.innerWidth >= 1024 ? 24 : 16);

export const HomeNews = ({data,loading}) => {
  const navigate = useNavigate();
  const language = useSelector((state) => state.language.value);
  const isRtl = language === "_ar";

  const [items, setItems] = useState(data || []);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(getPerView());
  const [gap, setGap] = useState(getGap());
  const touchStartX = useRef(null);

  useEffect(() => {
    setItems(data || []);
  }, [data]);

  // The home endpoint only returns the latest few; load the news list so the
  // carousel can page through more without leaving the homepage.
  useEffect(() => {
    const loadMore = async () => {
      try {
        const response = await getRequest(apiRoutes.get_news, { page: 1 });
        const list = response?.data?.data;
        if (response?.status === "success" && Array.isArray(list) && list.length > 0) {
          setItems((current) => {
            const seen = new Set(current.map((item) => item?.id));
            return [...current, ...list.filter((item) => !seen.has(item?.id))];
          });
        }
      } catch (error) {}
    };
    loadMore();
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPerView(getPerView());
      setGap(getGap());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIndex = Math.max(0, items.length - perView);
  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [index, maxIndex]);

  const canPrev = index > 0;
  const canNext = index < maxIndex;
  const goPrev = () => canPrev && setIndex((i) => Math.max(0, i - perView));
  const goNext = () => canNext && setIndex((i) => Math.min(maxIndex, i + perView));

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    // Swiping against the reading direction shows the next cards.
    const forward = isRtl ? delta > 0 : delta < 0;
    forward ? goNext() : goPrev();
  };

  const handleProjectClick = (id) => {
    navigate(`/news/${id}`);
  };
  const handleViewMore = () => {
    navigate(`/news`);
  };

  const shift = `calc(${isRtl ? "" : "-"}${index} * (100% + ${gap}px) / ${perView})`;
  const cardWidth = `calc((100% - ${(perView - 1) * gap}px) / ${perView})`;

  const arrowClass = (enabled) =>
    `flex justify-center items-center md:p-2 p-1.5 border-2 border-black rounded-full transition-opacity ${
      enabled ? "cursor-pointer hover:bg-black/5" : "opacity-30 cursor-not-allowed"
    }`;

  return (
    <div className="w-full h-full" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-primary sm:text-F32 text-F26 font-semibold">
          {GetAppText("news")}
        </h1>
        <div className="flex items-center sm:gap-5 gap-3">
          <button
            type="button"
            onClick={handleViewMore}
            className="flex items-center gap-1 text-primary sm:text-F18 text-F16 font-semibold hover:underline whitespace-nowrap"
          >
            {GetAppText("view_more")}
            <SvgComponent
              name={isRtl ? "ArrowLeft" : "ArrowRight"}
              className={"w-5 h-5"}
              fill={"none"}
              stroke={"#2E4049"}
            />
          </button>
        </div>
      </div>

      <div className="pt-8">
        {loading ? (
          <div className="flex h-[420px] w-full justify-center items-center">
            <Loader />
          </div>
        ) : items?.length > 0 ? (
          <div className="overflow-hidden pt-2 pb-10 -mt-2 -mb-10" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div
              className="flex items-stretch transition-transform duration-500 ease-in-out"
              style={{ gap: `${gap}px`, transform: `translateX(${shift})` }}
            >
              {items.map((item, i) => (
                <div
                  key={item?.id ?? i}
                  className="shrink-0"
                  style={{ width: cardWidth }}
                  aria-hidden={i < index || i >= index + perView}
                >
                  <NewsCardComponent
                    fluid
                    tabIndex={i < index || i >= index + perView ? -1 : 0}
                    onClick={() => {
                      handleProjectClick(item?.slug);
                    }}
                    data={item}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex w-full h-[300px] justify-center text-F20 font-semibold items-center">
            {GetAppText("no_data")}
          </div>
        )}
      </div>
    </div>
  );
};
